import { useEffect, useRef, useSyncExternalStore } from "react";
import * as d3 from "d3";
import { saveSvgAsPng } from "save-svg-as-png";
import workSansFontUrl from "@/assets/fonts/WorkSans-VariableFont_wght.ttf?url";
import type { LegendItem, ScenarioKey } from "./onboardingPlots.types";

export const FONT = "Work Sans";

const EXPORT_FONTS = [
  {
    text: `@font-face { font-family: "Work Sans"; src: url("${workSansFontUrl}") format("truetype"); }`,
    url: workSansFontUrl,
    format: "application/x-font-ttf",
  },
];

const SVG_NS = "http://www.w3.org/2000/svg";

const LEGEND_FONT = `12px ${FONT}`;
const LEGEND_MARGIN = 16;
const LEGEND_SWATCH_WIDTH = 16;
const LEGEND_SWATCH_GAP = 6;
const LEGEND_ITEM_GAP = 16;
const LEGEND_ROW_HEIGHT = 20;
const LEGEND_TOP_GAP = 12;
const LEGEND_BOTTOM_PADDING = 4;

const TITLE_FONT_SIZE = 15;
const HINT_FONT_SIZE = 12;
const TITLE_BLOCK_BOTTOM_PADDING = 12;

function measureTextWidth(text: string, font: string) {
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return text.length * 7;
  ctx.font = font;
  return ctx.measureText(text).width;
}

function prependTitleToSvgClone(
  svgClone: SVGSVGElement,
  width: number,
  title: string,
  hint?: string,
) {
  const titleBlockHeight =
    TITLE_FONT_SIZE +
    TITLE_BLOCK_BOTTOM_PADDING +
    (hint ? HINT_FONT_SIZE + 2 : 0);

  const contentGroup = document.createElementNS(SVG_NS, "g");
  while (svgClone.firstChild) contentGroup.appendChild(svgClone.firstChild);
  contentGroup.setAttribute("transform", `translate(0, ${titleBlockHeight})`);
  svgClone.appendChild(contentGroup);

  const titleText = document.createElementNS(SVG_NS, "text");
  titleText.setAttribute("x", `${LEGEND_MARGIN}`);
  titleText.setAttribute("y", `${TITLE_FONT_SIZE}`);
  titleText.setAttribute("fill", COLORS.ink);
  titleText.setAttribute("font-family", FONT);
  titleText.setAttribute("font-size", `${TITLE_FONT_SIZE}`);
  titleText.setAttribute("font-weight", "600");
  titleText.textContent = title;
  svgClone.appendChild(titleText);

  if (hint) {
    const titleWidth = measureTextWidth(
      title,
      `600 ${TITLE_FONT_SIZE}px ${FONT}`,
    );
    const hintText = document.createElementNS(SVG_NS, "text");
    hintText.setAttribute("x", `${width - LEGEND_MARGIN}`);
    hintText.setAttribute("y", `${TITLE_FONT_SIZE}`);
    hintText.setAttribute("text-anchor", "end");
    hintText.setAttribute("fill", COLORS.axis);
    hintText.setAttribute("font-family", FONT);
    hintText.setAttribute("font-size", `${HINT_FONT_SIZE}`);
    // Keep the hint on its own line if it would overlap the title.
    const sameLineAvailable = width - LEGEND_MARGIN * 2 - titleWidth - 12;
    hintText.setAttribute(
      "y",
      measureTextWidth(hint, `${HINT_FONT_SIZE}px ${FONT}`) <= sameLineAvailable
        ? `${TITLE_FONT_SIZE}`
        : `${TITLE_FONT_SIZE + HINT_FONT_SIZE + 2}`,
    );
    hintText.textContent = hint;
    svgClone.appendChild(hintText);
  }

  return titleBlockHeight;
}

function appendLegendToSvgClone(
  svgClone: SVGSVGElement,
  items: LegendItem[],
  width: number,
  topOffset: number,
) {
  if (!items.length) return 0;

  const availableWidth = width - LEGEND_MARGIN * 2;

  let xOffset = 0;
  let row = 0;
  const positioned = items.map((item) => {
    const itemWidth =
      LEGEND_SWATCH_WIDTH +
      LEGEND_SWATCH_GAP +
      measureTextWidth(item.label, LEGEND_FONT);
    if (xOffset > 0 && xOffset + itemWidth > availableWidth) {
      row += 1;
      xOffset = 0;
    }
    const position = { ...item, x: xOffset, y: row * LEGEND_ROW_HEIGHT };
    xOffset += itemWidth + LEGEND_ITEM_GAP;
    return position;
  });

  const legendGroup = document.createElementNS(SVG_NS, "g");
  legendGroup.setAttribute(
    "transform",
    `translate(${LEGEND_MARGIN}, ${topOffset + LEGEND_TOP_GAP})`,
  );

  positioned.forEach(({ color, label, dash, x, y }) => {
    if (dash) {
      const line = document.createElementNS(SVG_NS, "line");
      line.setAttribute("x1", `${x}`);
      line.setAttribute("x2", `${x + LEGEND_SWATCH_WIDTH}`);
      line.setAttribute("y1", `${y + 7}`);
      line.setAttribute("y2", `${y + 7}`);
      line.setAttribute("stroke", color);
      line.setAttribute("stroke-width", "3");
      line.setAttribute("stroke-dasharray", "5 3");
      legendGroup.appendChild(line);
    } else {
      const rect = document.createElementNS(SVG_NS, "rect");
      rect.setAttribute("x", `${x}`);
      rect.setAttribute("y", `${y + 2}`);
      rect.setAttribute("width", `${LEGEND_SWATCH_WIDTH}`);
      rect.setAttribute("height", "10");
      rect.setAttribute("rx", "2");
      rect.setAttribute("fill", color);
      legendGroup.appendChild(rect);
    }

    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", `${x + LEGEND_SWATCH_WIDTH + LEGEND_SWATCH_GAP}`);
    text.setAttribute("y", `${y + 11}`);
    text.setAttribute("fill", COLORS.axis);
    text.setAttribute("font-family", FONT);
    text.setAttribute("font-size", "12");
    text.textContent = label;
    legendGroup.appendChild(text);
  });

  svgClone.appendChild(legendGroup);
  return (row + 1) * LEGEND_ROW_HEIGHT + LEGEND_TOP_GAP + LEGEND_BOTTOM_PADDING;
}

export async function downloadChartSvg(
  containerId: string,
  title: string,
  opts: { hint?: string; legendItems?: LegendItem[] } = {},
) {
  const svg = document.querySelector(
    `#${containerId} svg`,
  ) as SVGSVGElement | null;
  if (!svg) return;
  if ("fonts" in document) await document.fonts.ready;

  const svgClone = svg.cloneNode(true) as SVGSVGElement;
  const viewBox = svgClone.viewBox?.baseVal;
  const width =
    (viewBox && viewBox.width) || Number(svgClone.getAttribute("width")) || 600;
  const chartHeight =
    (viewBox && viewBox.height) ||
    Number(svgClone.getAttribute("height")) ||
    300;

  const titleBlockHeight = prependTitleToSvgClone(
    svgClone,
    width,
    title,
    opts.hint,
  );
  const legendBlockHeight = appendLegendToSvgClone(
    svgClone,
    opts.legendItems ?? [],
    width,
    titleBlockHeight + chartHeight,
  );

  const totalHeight = titleBlockHeight + chartHeight + legendBlockHeight;
  svgClone.setAttribute("width", `${width}`);
  svgClone.setAttribute("height", `${totalHeight}`);
  svgClone.setAttribute(
    "viewBox",
    `${viewBox?.x ?? 0} ${viewBox?.y ?? 0} ${width} ${totalHeight}`,
  );

  const date = new Date().toISOString().split("T")[0].replaceAll("-", "");
  const filename = `${date}_${title}.png`;
  await saveSvgAsPng(svgClone, filename, {
    fonts: EXPORT_FONTS,
    backgroundColor: "#ffffff",
  });
}

export const COLORS = {
  actual: "#0C98D0",
  actualFill: "rgba(12,152,208,0.12)",
  forecast: "#6C4AB6",
  forecastFill: "rgba(108,74,182,0.22)",
  red: "#980000",
  redFill: "rgba(152,0,0,0.18)",
  good: "#1f9d63",
  axis: "#6b8089",
  grid: "#eef2f4",
  gridLine: "#dbe3e7",
  ink: "#2B4752",
  tooltipBg: "#20353E",
  screenedBar: "#64BDE1",
  paceBar: "#9ed4ea",
  sfBar: "#f0b8b8",
};

export const SCENARIOS: {
  key: ScenarioKey;
  label: string;
  rate: number;
  color: string;
}[] = [
  { key: "stretch", label: "Stretch 0.8", rate: 0.8, color: "#177245" },
  { key: "target", label: "Target 0.7", rate: 0.7, color: "#991962" },
  { key: "baseline", label: "Baseline 0.6", rate: 0.6, color: "#C9922B" },
  { key: "minimum", label: "Minimum 0.5", rate: 0.5, color: "#8C8C8C" },
];

export const parseMonth = d3.timeParse("%Y-%m-%d");
export const fmtMonth = d3.timeFormat("%b '%y");
export const fmtMonthLong = d3.timeFormat("%b %Y");

export function useChartWidth(fallback = 600) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useSyncExternalStore(
    (onStoreChange) => {
      const el = ref.current;
      if (!el) return () => {};

      const update = () => onStoreChange();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      window.addEventListener("resize", update);
      return () => {
        ro.disconnect();
        window.removeEventListener("resize", update);
      };
    },
    () => ref.current?.clientWidth || fallback,
    () => fallback,
  );
  return { ref, width };
}

export type Margin = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export function clearSvg(node: SVGSVGElement | null) {
  if (node) d3.select(node).selectAll("*").remove();
}

export function topRoundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  if (height <= 0 || width <= 0) return "";
  const r = Math.max(0, Math.min(radius, width / 2, height));
  return `M${x},${y + height} V${y + r} Q${x},${y} ${x + r},${y} H${x + width - r} Q${x + width},${y} ${x + width},${y + r} V${y + height} Z`;
}

export function monthKey(date: string) {
  return date.slice(0, 7);
}

export function useD3Chart(
  draw: (svg: any, tip: ReturnType<typeof makeTooltip>, width: number) => void,
) {
  const { ref, width } = useChartWidth();
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svgNode = svgRef.current;
    const container = ref.current;
    if (!svgNode || !container || width === 0) return;
    clearSvg(svgNode);
    const tip = makeTooltip(container);
    draw(d3.select(svgNode), tip, width);
    return () => tip.remove();
  }, [width, ref, draw]);

  return { ref, svgRef, width };
}

export function drawTimeAxes(
  svg: any,
  W: number,
  H: number,
  m: Margin,
  x: any,
  y: any,
  yTickFormat?: (v: any) => string,
  sparseTicks?: boolean,
) {
  svg
    .append("g")
    .attr("transform", `translate(${m.left},0)`)
    .call(
      d3
        .axisLeft(y)
        .ticks(5)
        .tickSize(-(W - m.left - m.right))
        .tickFormat(() => ""),
    )
    .call((g: any) => g.select(".domain").remove())
    .call((g: any) => g.selectAll(".tick line").attr("stroke", COLORS.grid));

  const [d0, d1] = x.domain();
  let tickValues: Date[] | undefined;
  if (sparseTicks) {
    const monthSpan = d3.timeMonth.count(d0, d1) + 1;
    const tickEvery = Math.max(4, Math.ceil(monthSpan / 4));
    // Step from the domain's actual start date rather than `.every(k)`,
    // which anchors to the calendar epoch and can land on the same month
    // (e.g. always January) instead of spacing evenly from the data.
    tickValues = [];
    for (let d = d0; d <= d1; d = d3.timeMonth.offset(d, tickEvery)) {
      tickValues.push(d);
    }
  }

  svg
    .append("g")
    .attr("transform", `translate(0,${H - m.bottom})`)
    .call(
      d3
        .axisBottom(x)
        .tickValues(tickValues ?? d3.timeMonth.every(3).range(d0, d1))
        .tickFormat(fmtMonth as any),
    )
    .call(styleAxis);
  svg
    .append("g")
    .attr("transform", `translate(${m.left},0)`)
    .call(
      d3
        .axisLeft(y)
        .ticks(5)
        .tickFormat(yTickFormat as any),
    )
    .call(styleAxis);
}

export function styleAxis(g: any) {
  g.selectAll("text")
    .attr("fill", COLORS.axis)
    .style("font-size", "11px")
    .style("font-family", FONT);
  g.selectAll(".domain, .tick line").attr("stroke", COLORS.gridLine);
}

export function drawBandAxes(
  svg: any,
  W: number,
  H: number,
  m: Margin,
  x: d3.ScaleBand<string>,
  y: any,
  yTickFormat?: (v: any) => string,
) {
  svg
    .append("g")
    .attr("transform", `translate(${m.left},0)`)
    .call(
      d3
        .axisLeft(y)
        .ticks(5)
        .tickSize(-(W - m.left - m.right))
        .tickFormat(() => ""),
    )
    .call((g: any) => g.select(".domain").remove())
    .call((g: any) => g.selectAll(".tick line").attr("stroke", COLORS.grid));
  svg
    .append("g")
    .attr("transform", `translate(0,${H - m.bottom})`)
    .call(d3.axisBottom(x).tickFormat((mm: any) => mm.slice(2)))
    .call(styleAxis)
    .selectAll("text")
    .attr("transform", "rotate(-40)")
    .style("text-anchor", "end");
  svg
    .append("g")
    .attr("transform", `translate(${m.left},0)`)
    .call(
      d3
        .axisLeft(y)
        .ticks(5)
        .tickFormat(yTickFormat as any),
    )
    .call(styleAxis);
}

export function drawBandBars<T extends { mm: string }>(
  svg: any,
  data: T[],
  x: d3.ScaleBand<string>,
  y: any,
  valueOf: (d: T) => number | null,
  fill: string,
  tip: ReturnType<typeof makeTooltip>,
  tooltipHtml: (d: T) => string,
) {
  const bars = data.filter((d) => valueOf(d) != null);
  svg
    .selectAll("path.bar")
    .data(bars)
    .join("path")
    .attr("class", "bar")
    .attr("d", (r: T) =>
      topRoundedRectPath(
        x(r.mm) as number,
        y(valueOf(r) as number),
        x.bandwidth(),
        y(0) - y(valueOf(r) as number),
        4,
      ),
    )
    .attr("fill", fill)
    .on("mousemove", (ev: MouseEvent, r: T) => tip.show(tooltipHtml(r), ev))
    .on("mouseleave", () => tip.hide());
}

export function drawCutoff(
  svg: any,
  cutoffDate: Date,
  m: Margin,
  H: number,
  x: any,
) {
  svg
    .append("line")
    .attr("x1", x(cutoffDate))
    .attr("x2", x(cutoffDate))
    .attr("y1", m.top)
    .attr("y2", H - m.bottom)
    .attr("stroke", COLORS.tooltipBg)
    .attr("stroke-dasharray", "4 4")
    .attr("stroke-width", 1)
    .attr("opacity", 0.5);
  svg
    .append("text")
    .attr("x", x(cutoffDate) + 4)
    .attr("y", m.top + 10)
    .attr("fill", COLORS.axis)
    .style("font-size", "10.5px")
    .style("font-weight", 600)
    .style("font-family", FONT)
    .text("data cutoff");
}

// `container` must have position: relative for the absolute-positioned tooltip to anchor correctly.
export function makeTooltip(container: HTMLElement) {
  const el = d3
    .select(container)
    .append("div")
    .attr("class", "onboarding-tip")
    .style("position", "absolute")
    .style("pointer-events", "none")
    .style("opacity", "0")
    .style("background", "white")
    .style("color", COLORS.ink)
    .style("border", "1px solid #ddd")
    .style("padding", "10px")
    .style("border-radius", "5px")
    .style("font", `12px ${FONT}`)
    .style("line-height", "1.5")
    .style("transition", "opacity 0.3s")
    .style("z-index", "1000")
    .style("max-width", "240px");
  return {
    show(html: string, ev: MouseEvent) {
      const b = container.getBoundingClientRect();
      el.html(html)
        .style("opacity", "0.9")
        .style("left", `${ev.clientX - b.left + 14}px`)
        .style("top", `${ev.clientY - b.top + 14}px`);
    },
    hide() {
      el.style("opacity", "0");
    },
    remove() {
      el.remove();
    },
  };
}
